import React, { useState, useEffect, useRef } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Select from "react-select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { MdStore, MdSearch } from "react-icons/md";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  multiValue: (base) => ({ ...base, background: "#e0e7ff", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#3730a3", fontSize: "0.8rem", fontWeight: 500 }),
  multiValueRemove: (base) => ({ ...base, color: "#6366f1", cursor: "pointer", "&:hover": { background: "#c7d2fe", color: "#3730a3" } }),
};

const SellerListTab = ({ brandId, protectedFetch }) => {
  const { showToast } = useToast();
  const [assignedSellers, setAssignedSellers] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const fetchedRef = useRef(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const parseResponse = (res) => {
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.results)) return res.results;
    // If it's a Response object (error case from checkResponseThenJson), return empty
    if (res && typeof res.json === "function") return [];
    return [];
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);

    Promise.all([
      protectedFetch(
        apiEndpoints.seller.list() + `?brand_id=${brandId}`,
        "GET",
        null,
        errorFunctions.checkResponseThenJson,
      ),
      protectedFetch(
        apiEndpoints.seller.list(),
        "GET",
        { limit: 1000 }, // Fetch all sellers for selection
        errorFunctions.checkResponseThenJson,
      ),
    ])
      .then(([brandRes, allRes]) => {
        setAssignedSellers(parseResponse(brandRes));
        setAllSellers(parseResponse(allRes));
      })
      .catch((err) => {
        console.error("Failed to load sellers", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId]);

  const assignedIds = assignedSellers.map((s) => s.id);

  const sellerOptions = allSellers
    .filter((s) => !assignedIds.includes(s.id))
    .map((s) => ({
      value: s.id,
      label: `${s.store_name || s.name || s.id}${s.location ? ` — ${s.location}` : ""}`,
      seller: s,
    }));

  const filterOption = (option, input) => {
    if (!input) return true;
    const term = input.toLowerCase();
    return (
      option.label.toLowerCase().includes(term) ||
      (option.seller?.location || "").toLowerCase().includes(term)
    );
  };

  const handleAddSelected = () => {
    if (selectedOptions.length === 0) return;
    const newIds = selectedOptions
      .filter((o) => !assignedIds.includes(o.value))
      .map((o) => o.value);

    const toAdd = selectedOptions
      .filter((o) => newIds.includes(o.value) && o.seller)
      .map((o) => o.seller);

    if (toAdd.length > 0) {
      setAssignedSellers((prev) => [...prev, ...toAdd]);
    }
    setSelectedOptions([]);
  };

  // const handleRemoveSeller = (sellerId) => {
  //   setAssignedSellers((prev) => prev.filter((s) => s.id !== sellerId));
  // };

  const handleSave = () => {
    setSaving(true);
    const sellerIds = assignedSellers.map((s) => s.id);
    protectedFetch(
      apiEndpoints.seller.sellerAssignBrands(),
      "POST",
      { seller_ids: sellerIds, brand_ids: [brandId] },
      errorFunctions.checkResponseThenJson,
    )
      .then(() => {
        showToast("Sellers saved successfully", "success");
      })
      .catch(() => {
        showToast("Failed to save sellers", "error");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAssigned = searchTerm.trim()
    ? assignedSellers.filter((s) => {
        const name = (s.store_name || s.name || "").toLowerCase();
        const loc = (s.location || "").toLowerCase();
        const term = searchTerm.toLowerCase();
        return name.includes(term) || loc.includes(term);
      })
    : assignedSellers;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div>
      {/* Add sellers section */}
      <div
        style={{
          padding: 16,
          marginBottom: 20,
          borderRadius: 10,
          border: "1px solid #e5e7eb",
          background: "#f9fafb",
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: "0.85rem",
                color: "#374151",
                marginBottom: 6,
              }}
            >
              Select Sellers
            </label>
            <Select
              options={sellerOptions}
              value={selectedOptions}
              onChange={(val) => setSelectedOptions(val || [])}
              placeholder="Choose sellers to add..."
              isMulti
              isClearable={false}
              styles={selectStyles}
              filterOption={filterOption}
              noOptionsMessage={() => "No sellers available"}
            />
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddSelected}
            disabled={selectedOptions.length === 0}
            style={{
              borderRadius: 8,
              padding: "7px 18px",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Add Selected{selectedOptions.length > 0 ? ` (${selectedOptions.length})` : ""}
          </button>
        </div>
      </div>

      {/* Assigned sellers table */}
      {assignedSellers.length > 0 ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1f2937" }}>
                Assigned Sellers
              </span>
              <span
                style={{
                  background: "#e0e7ff",
                  color: "#3730a3",
                  padding: "1px 10px",
                  borderRadius: 12,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                {assignedSellers.length}
              </span>
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 280 }}>
              <span
                className="input-group-text"
                style={{
                  background: "white",
                  borderRight: "none",
                  borderRadius: "8px 0 0 8px",
                }}
              >
                <MdSearch size={16} color="#9ca3af" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search assigned sellers..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                style={{ borderLeft: "none", borderRadius: "0 8px 8px 0" }}
              />
            </div>
          </div>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ background: "#f9fafb" }}>
                    <TableCell style={{ fontWeight: 700 }}>Store Name</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Country</TableCell>
                    {/* <TableCell style={{ fontWeight: 700, textAlign: "right" }}>
                      Actions
                    </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAssigned.length > 0 ? (
                    filteredAssigned
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((seller) => (
                        <TableRow key={seller.id}>
                          <TableCell>
                            {seller.store_name || seller.name || "—"}
                          </TableCell>
                          <TableCell>{seller.country && seller.country.length > 0 ? seller.country.join(',') : "—"}</TableCell>
                          {/* <TableCell style={{ textAlign: "right" }}>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveSeller(seller.id)}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                                borderRadius: 6,
                                padding: "4px 10px",
                                fontSize: "0.8rem",
                              }}
                            >
                              <MdDelete size={13} /> Remove
                            </button>
                          </TableCell> */}
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} style={{ textAlign: "center", padding: "32px 16px" }}>
                        <p style={{ color: "#6b7280", margin: 0 }}>
                          No sellers matching &ldquo;{searchTerm}&rdquo;
                        </p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 25, 100]}
              component="div"
              count={filteredAssigned.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiTablePagination-toolbar": { alignItems: "center" },
                ".MuiTablePagination-selectLabel": { marginBottom: 0 },
                ".MuiTablePagination-displayedRows": { marginBottom: 0 },
              }}
            />
          </Paper>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
              style={{
                borderRadius: 8,
                padding: "7px 28px",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "48px 16px" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#f3f4f6",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <MdStore size={28} color="#9ca3af" />
          </div>
          <p style={{ color: "#6b7280", fontWeight: 500, marginBottom: 4 }}>
            No sellers assigned
          </p>
          <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
            Use the selector above to add sellers to this brand
          </p>
        </div>
      )}
    </div>
  );
};

export default SellerListTab;
